export type DropshippingProfitInputs = {
  adSpendPercent: number;
  appsCost: number;
  monthlyOrders: number;
  productCost: number;
  returnsPercent: number;
  sellingPrice: number;
  shippingCost: number;
  transactionFeeFixed: number;
  transactionFeePercent: number;
};

export type DropshippingProfitResult = {
  adSpend: number;
  /** Minimum return on ad spend that keeps this order from being a loss. Null when there is no profit before ads to spend against. */
  breakEvenRoas: number | null;
  monthlyProfit: number;
  monthlyRevenue: number;
  preAdSpendProfit: number;
  profitMarginPercent: number;
  profitPerOrder: number;
  returnsReserve: number;
  /** Profit ÷ (product + shipping cost). Null when that base cost is zero. */
  roiPercent: number | null;
  totalCostPerOrder: number;
  transactionFee: number;
};

const PERCENT_FIELDS = new Set<keyof DropshippingProfitInputs>([
  'adSpendPercent',
  'returnsPercent',
  'transactionFeePercent',
]);

export function normalizeDropshippingProfitInput(
  key: keyof DropshippingProfitInputs,
  value: string,
) {
  const numericValue = Number.parseFloat(value);

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  if (numericValue < 0) {
    return '0';
  }

  if (PERCENT_FIELDS.has(key) && numericValue > 100) {
    return '100';
  }

  if (key === 'monthlyOrders') {
    return String(Math.floor(numericValue));
  }

  return value;
}

function asNonNegative(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function asPercent(value: number) {
  return Math.min(100, asNonNegative(value));
}

function roundCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function roundPercent(value: number) {
  return Math.round((value + Number.EPSILON) * 10) / 10;
}

export function calculateDropshippingProfit(
  input: DropshippingProfitInputs,
): DropshippingProfitResult {
  const sellingPrice = asNonNegative(input.sellingPrice);
  const productCost = asNonNegative(input.productCost);
  const shippingCost = asNonNegative(input.shippingCost);
  const appsCost = asNonNegative(input.appsCost);
  const monthlyOrders = asNonNegative(input.monthlyOrders);
  const transactionFeeFixed = asNonNegative(input.transactionFeeFixed);
  const transactionFeePercent = asPercent(input.transactionFeePercent);
  const adSpendPercent = asPercent(input.adSpendPercent);
  const returnsPercent = asPercent(input.returnsPercent);

  const transactionFee = roundCurrency(
    sellingPrice * (transactionFeePercent / 100) + transactionFeeFixed,
  );
  const adSpend = roundCurrency(sellingPrice * (adSpendPercent / 100));
  const returnsReserve = roundCurrency(sellingPrice * (returnsPercent / 100));
  const baseCostPerOrder = roundCurrency(productCost + shippingCost);
  const preAdSpendProfit = roundCurrency(
    sellingPrice -
      baseCostPerOrder -
      transactionFee -
      returnsReserve -
      appsCost,
  );
  const profitPerOrder = roundCurrency(preAdSpendProfit - adSpend);
  const totalCostPerOrder = roundCurrency(sellingPrice - profitPerOrder);
  const profitMarginPercent =
    sellingPrice > 0 ? roundPercent((profitPerOrder / sellingPrice) * 100) : 0;
  const roiPercent =
    baseCostPerOrder > 0
      ? roundPercent((profitPerOrder / baseCostPerOrder) * 100)
      : null;
  const breakEvenRoas =
    preAdSpendProfit > 0
      ? Math.round((sellingPrice / preAdSpendProfit) * 100) / 100
      : null;

  return {
    adSpend,
    breakEvenRoas,
    monthlyProfit: roundCurrency(profitPerOrder * monthlyOrders),
    monthlyRevenue: roundCurrency(sellingPrice * monthlyOrders),
    preAdSpendProfit,
    profitMarginPercent,
    profitPerOrder,
    returnsReserve,
    roiPercent,
    totalCostPerOrder,
    transactionFee,
  };
}
